import React, { useState } from 'react';

import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { CardContent, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { BaseColorKeys, SubjectColorKeys } from '../../theme';
import Link from '../Link';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ArrowIcon,
  CardContainer,
  TitleContainer,
  TitleDivider,
  TitleIcon,
  TitleStack,
  TopicLabel,
  VideoIndex,
  VideoLabel,
  VideosList,
  VideosListEntry,
} from './index.styles';

interface Video {
  subject: string,
  videoId: string,
  title: string,
}

interface TopicEntry {
  label: string,
  key: string,
  videos?: Video[]
}

interface VideoCategoryCardProps {
  // Heading label for the category
  categoryLabel: string,
  // Runs when a category is clicked
  onCategoryClick?: () => void,
  // Runs when a video link is clicked
  getVideoHref: (subject: string, videoId: string) => string,
  // List of topics and corresponding videos under each topic
  topics?: TopicEntry[],
  // Key of the topic to open on load, none by default
  openOnLoad?: string | false,
  // Color theme for the card
  color?: BaseColorKeys | SubjectColorKeys
}

const VideoCategoryCard = ({
  categoryLabel,
  topics = [],
  onCategoryClick = () => {},
  getVideoHref,
  openOnLoad = false,
  color = 'primary',
}: VideoCategoryCardProps) => {
  const [expanded, setExpanded] = useState<string | false>(openOnLoad);
  const onExpand =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
      onCategoryClick();
    };

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <CardContainer color={color}>
      <CardContent>
        <TitleContainer>
          <TitleIcon color={color}>
            <PlayArrowRoundedIcon />
          </TitleIcon>
          <TitleStack>
            <Typography variant="h5" component="div">
              {categoryLabel.split(':')[0]}
            </Typography>
            {categoryLabel.split(':').length > 1 && (
              <Typography variant="caption" component="div">
                {categoryLabel.split(':')[1]}
              </Typography>
            )}
          </TitleStack>
        </TitleContainer>
        <TitleDivider />
        {topics.length > 0 ? (
          <div>
            {topics.map((topic) => (
              <Accordion
                key={topic.key}
                expanded={expanded === topic.key}
                onChange={onExpand(topic.key)}
                disableGutters
                elevation={0}
                square
              >
                <AccordionSummary
                  aria-controls={`${topic.key}-content`}
                  id={`${topic.key}-header`}
                  expandIcon={(
                    <ArrowIcon $expanded={expanded === topic.key} />
                  )}
                >
                  <TopicLabel selected={expanded === topic.key}>{topic.label}</TopicLabel>
                </AccordionSummary>
                <AccordionDetails>
                  {topic?.videos && topic.videos.length > 0 ? (
                    <VideosList isSingleColumn={!isMd}>
                      {(topic.videos || []).map((video, index) => (
                        <VideosListEntry key={video.videoId}>
                          <VideoIndex variant="body2">
                            {index + 1}
                          </VideoIndex>
                          <VideoLabel variant="body2">
                            <Link href={getVideoHref(video.subject, video.videoId)} underline="none" color="inherit">
                              {video.title}
                            </Link>
                          </VideoLabel>
                        </VideosListEntry>
                      ))}
                    </VideosList>
                  ) : (
                    <Typography variant="caption">
                      There are no videos available under this topic yet.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        ) : (
          <Typography variant="caption">
            There are no topics available under this category yet.
          </Typography>
        )}
      </CardContent>
    </CardContainer>
  );
};

export default VideoCategoryCard;
